/**
 * @engine/vfx - Update Context Nodes
 *
 * Nodes for updating particles each frame.
 */

import type { VFXNodeDefinition } from '../../types';
import { dataPort, flowPort } from '../../types';

/**
 * Update context node definitions.
 */

export const KILL_COLLISION_NODES: readonly VFXNodeDefinition[] = [
  // ---- Collision (Plane) ----
  {
    id: 'vfx.update.collision_plane',
    name: 'Collision (Plane)',
    category: 'collision',
    contexts: ['update'],
    description: 'Bounce particles off a plane',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('point', 'Point', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('normal', 'Normal', 'vec3', { defaultValue: { x: 0, y: 1, z: 0 } }),
      dataPort('bounce', 'Bounce', 'float', { defaultValue: 0.5, range: { min: 0, max: 1 } }),
      dataPort('friction', 'Friction', 'float', { defaultValue: 0.2, range: { min: 0, max: 1 } }),
    ],
    outputs: [
      flowPort('next', ''),
      flowPort('on_collision', 'On Collision'),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let dist = dot(particle.position - point, normal);
        if (dist < 0.0) {
          particle.position -= normal * dist * 2.0;
          let vn = normal * dot(particle.velocity, normal);
          let vt = particle.velocity - vn;
          particle.velocity = vt * (1.0 - friction) - vn * bounce;
        }
      `,
    },
  },

  // ---- Kill (Age) ----
  {
    id: 'vfx.update.kill_age',
    name: 'Kill (Age)',
    category: 'utility',
    contexts: ['update'],
    description: 'Kill particles when they exceed lifetime',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
    ],
    outputs: [
      flowPort('next', ''),
      flowPort('on_death', 'On Death'),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        if (particle.lifetime >= particle.maxLifetime) {
          particle.alive = 0u;
        }
      `,
    },
  },

  // ---- Kill (Bounds) ----
  {
    id: 'vfx.update.kill_bounds',
    name: 'Kill (Bounds)',
    category: 'utility',
    contexts: ['update'],
    description: 'Kill particles outside bounds',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('min', 'Min', 'vec3', { defaultValue: { x: -10, y: -10, z: -10 } }),
      dataPort('max', 'Max', 'vec3', { defaultValue: { x: 10, y: 10, z: 10 } }),
    ],
    outputs: [
      flowPort('next', ''),
      flowPort('on_death', 'On Death'),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let belowMin = particle.position.x < min.x || particle.position.y < min.y || particle.position.z < min.z;
        let aboveMax = particle.position.x > max.x || particle.position.y > max.y || particle.position.z > max.z;
        if (belowMin || aboveMax) {
          particle.alive = 0u;
        }
      `,
    },
  },
];
